import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getDocumentUrl, fetchSignRequest } from "../../SignRequestService";
import { RequestFile } from "models/interfaces";
import PDFEditor from "./PDFHighlighter";
import type { IHighlight } from "react-pdf-highlighter";
import history from "utils/history";
import { declineRequest } from "../../SignRequestService";
import { openSuccessNotification, handleErrorMessage } from "helpers/methods";
import { useAppDispatch } from "redux/hooks";
import { setLoading } from "redux/appSlice";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import declineRequestTokens from "../DeclineRequest.tokens";
import { IS_EMBED } from "embed/embedMode";
import { postEmbedEventOnce } from "embed/embedBridge";
import { EmbedDone } from "embed/EmbedPanel";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CaretDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileIcon,
  HighlighterIcon,
  SwatchCheckIcon,
} from "./SignerExperienceIcons";

const t = declineRequestTokens;
const c = t.palette;

const COMMON_REASONS = [
  "Wrong document",
  "Terms need changes",
  "Not the right signer",
  "Need more time",
];

/** Native button reset — every control on this screen is drawn from tokens. */
const buttonReset = {
  appearance: "none",
  border: "none",
  background: "none",
  padding: 0,
  margin: 0,
  fontFamily: "inherit",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
} as const;

const DeclineRequestPage: React.FC = () => {
  useTitle(title.DECLINE_REQUEST);
  const urlParams = window.location.search;
  const requestToken = new URLSearchParams(urlParams).get("id");
  const dispatch = useAppDispatch();
  const theme = useTheme();
  // Clause-highlight swatches — signer annotation palette (yellow/orange/red/blue).
  const swatches = useMemo(
    () => [
      { ...theme.palette.signer.highlight.yellow, name: "Yellow" },
      { ...theme.palette.signer.highlight.orange, name: "Orange" },
      { ...theme.palette.signer.highlight.red, name: "Red" },
      { ...theme.palette.signer.highlight.blue, name: "Blue" },
    ],
    [theme],
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [pdfFiles, setPdfFiles] = useState<RequestFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<RequestFile | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [loading, setPdfLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [declineLoading, setDeclineLoading] = useState(false);
  // Embedded only: the decline went through. The widget stays here on a fixed panel; going back would
  // reload the signing screen, which re-announces itself and reports the (now declined) request as an error.
  const [embedDeclined, setEmbedDeclined] = useState(false);
  const [reason, setReason] = useState("");
  const [senderName, setSenderName] = useState("");
  const [docMenuAnchor, setDocMenuAnchor] = useState<null | HTMLElement>(null);
  const [highlightsByFile, setHighlightsByFile] = useState<
    Map<string, IHighlight[]>
  >(new Map());
  const [viewerVersion, setViewerVersion] = useState(0);
  const [isDocumentsDrawerOpen, setIsDocumentsDrawerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(swatches[0].main);

  const highlightsByFileRef = useRef<Map<string, IHighlight[]>>(new Map());
  useEffect(() => {
    highlightsByFileRef.current = highlightsByFile;
  });

  const pdfContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (requestToken) {
      const navState: any = (history.location as any)?.state;
      if (
        navState?.pdfFiles &&
        Array.isArray(navState.pdfFiles) &&
        navState.pdfFiles.length > 0
      ) {
        const normalizedFiles = normalizeFiles(navState.pdfFiles).map(
          (f: RequestFile) => ({
            ...f,
            unsignedDocUrl: "",
            unsignedPdfUrl: f.unsignedPdfUrl || "",
          }),
        );
        setPdfFiles(normalizedFiles);
        // Auto-select the first file or the file from navigation state
        const initialSelectedBase = navState.selectedFile
          ? normalizeFile(navState.selectedFile)
          : normalizedFiles[0];
        const initialSelected = initialSelectedBase
          ? {
              ...initialSelectedBase,
              unsignedDocUrl: "",
              unsignedPdfUrl: initialSelectedBase.unsignedPdfUrl || "",
            }
          : null;
        setSelectedFile(initialSelected || null);
        setPageLoading(false);
        return;
      }

      fetchSignRequestData();
    }
  }, [requestToken]);

  const normalizeFile = (file: RequestFile): RequestFile => {
    const raw = (file.unsignedPdf || file.unsignedDoc || "") as string;
    const idx = raw.indexOf("/");
    const normalizedUnsignedDoc = idx >= 0 ? raw.substring(idx + 1) : raw;
    return {
      ...file,
      unsignedDoc: normalizedUnsignedDoc || file.unsignedDoc,
    };
  };

  const normalizeFiles = (files: RequestFile[]): RequestFile[] => {
    return files.map(normalizeFile);
  };

  const fetchSignRequestData = async () => {
    if (!requestToken) return;
    try {
      setPageLoading(true);
      const signRequest = await fetchSignRequest(requestToken);
      setSenderName(signRequest?.requestedBy?.name || "");

      if (signRequest && signRequest.files) {
        const normalizedFiles = normalizeFiles(signRequest.files);
        setPdfFiles(normalizedFiles);
        // Auto-select the first file
        if (normalizedFiles.length > 0) {
          setSelectedFile(normalizedFiles[0]);
        }
      }
    } catch (error: any) {
      console.error("Error fetching sign request:", error);
      handleErrorMessage(error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFile || !requestToken) return;

    const fetchPdfUrl = async () => {
      try {
        setPdfLoading(true);
        const response = await getDocumentUrl(
          selectedFile,
          true,
          requestToken,
          true,
        );
        setPdfUrl(response.message);
      } catch (error) {
        console.error("Error fetching PDF URL in DeclineRequestPage:", error);
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdfUrl();
  }, [selectedFile, requestToken]);

  const handleFileSelect = (file: RequestFile) => {
    setSelectedFile(file);
    // Close the documents drawer when a document is selected
    if (isMobile) {
      setIsDocumentsDrawerOpen(false);
    }
  };

  const getFileId = (file: RequestFile): string => {
    return file.unsignedPdf || file.unsignedDoc || "";
  };

  const updateCurrentFileHighlights = useCallback(
    (newHighlights: IHighlight[]) => {
      if (!selectedFile) return;
      const fileId = getFileId(selectedFile);
      const currentHighlights = highlightsByFileRef.current.get(fileId) || [];
      if (JSON.stringify(currentHighlights) === JSON.stringify(newHighlights)) {
        return;
      }
      const newHighlightsMap = new Map(highlightsByFileRef.current);
      newHighlightsMap.set(fileId, newHighlights);
      setHighlightsByFile(newHighlightsMap);
    },
    [selectedFile],
  );

  const currentFileKey = selectedFile
    ? `${getFileId(selectedFile)}-${viewerVersion}`
    : pdfUrl;

  const frozenInitialHighlights = useMemo(() => {
    if (!selectedFile) return [];
    const fileId = getFileId(selectedFile);
    return highlightsByFile.get(fileId) || [];
  }, [currentFileKey]);

  const clearAllHighlights = () => {
    setHighlightsByFile(new Map());
    setViewerVersion((v) => v + 1);
  };

  const totalHighlights = Array.from(highlightsByFile.values()).reduce(
    (sum, h) => sum + h.length,
    0,
  );
  const docsWithFlags = Array.from(highlightsByFile.values()).filter(
    (h) => h.length > 0,
  ).length;
  const hasFlags = totalHighlights > 0;
  const flagLabel = hasFlags
    ? `${totalHighlights === 1 ? "clause" : "clauses"} flagged across ${docsWithFlags} ${
        docsWithFlags === 1 ? "document" : "documents"
      }`
    : "no clauses flagged yet";
  const canDecline = Boolean(reason.trim()) && !loading && !declineLoading;

  const handleDecline = async () => {
    if (!reason.trim() || !requestToken) return;

    try {
      setDeclineLoading(true);
      dispatch(setLoading(true));

      const declineRequestMap: Record<string, { highlights: any[] }> = {};
      pdfFiles.forEach((file) => {
        const fileId = getFileId(file);
        const highlights = highlightsByFile.get(fileId) || [];

        const simplifiedHighlights =
          highlights.length > 0
            ? highlights
                .flatMap((h) => {
                  const position = h.position;
                  if (!position) return [];

                  const highlightColor = (h.comment as any)?.data?.color;

                  const rectsToUse =
                    position.rects && position.rects.length > 0
                      ? position.rects
                      : position.boundingRect
                        ? [position.boundingRect]
                        : [];

                  const pageNumber =
                    position.pageNumber || position.boundingRect?.pageNumber;
                  if (!pageNumber) return [];

                  return rectsToUse.map((rect: any) => ({
                    x1: rect.x1,
                    y1: rect.y1,
                    x2: rect.x2,
                    y2: rect.y2,
                    width: rect.width,
                    height: rect.height,
                    pageNumber: pageNumber,
                    color: highlightColor,
                  }));
                })
                .filter((h) => h != null)
            : [];

        const documentName = file.unsignedPdf || file.unsignedDoc || fileId;
        declineRequestMap[documentName] = {
          highlights: simplifiedHighlights,
        };
      });

      await declineRequest(requestToken, reason, declineRequestMap);

      // Embedded: only the fact of the decline goes to the partner page -- the reason stays server-side.
      if (IS_EMBED) {
        postEmbedEventOnce("sign_declined", { signerRole: "Signer" });
        setEmbedDeclined(true);
        return;
      }
      openSuccessNotification("Request declined successfully!");
      history.goBack();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setDeclineLoading(false);
      dispatch(setLoading(false));
    }
  };

  // ---------------------------------------------------------------------------
  // Right control rail — highlight palette, flag summary, reason, submit.
  // Rendered inline (not as a nested component) so typing in the reason field
  // doesn't remount the subtree and drop focus on every keystroke.
  // ---------------------------------------------------------------------------
  const renderRail = () => (
    <Box
      sx={{
        width: isMobile ? "100%" : t.rail.width,
        height: "100%",
        flexShrink: 0,
        boxSizing: isMobile ? "border-box" : t.rail.boxSizing,
        background: t.rail.background,
        borderLeft: isMobile ? "none" : t.rail.borderLeft,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1,
            py: 1,
            borderBottom: t.header.borderBottom,
          }}
        >
          <Box
            component="button"
            onClick={() => setIsDocumentsDrawerOpen(false)}
            sx={{
              ...buttonReset,
              width: t.header.backSize,
              height: t.header.backSize,
              borderRadius: t.header.backRadius,
              color: t.header.backColor,
              "&:hover": { background: t.header.backHoverBg },
            }}
          >
            <CloseIcon sx={{ fontSize: t.header.backIconSize }} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: t.rail.padding,
          display: "flex",
          flexDirection: "column",
          gap: t.rail.sectionGap,
        }}
      >
        {/* Highlight color */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: t.swatches.blockGap,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: t.rail.labelFontSize,
              fontWeight: t.rail.labelFontWeight,
              color: t.rail.labelColor,
              lineHeight: t.lineHeight,
            }}
          >
            Highlight color
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: t.swatches.rowGap,
            }}
          >
            {swatches.map((swatch) => {
              const active = selectedColor === swatch.main;
              return (
                <Box
                  key={swatch.main}
                  component="button"
                  type="button"
                  title={swatch.main}
                  aria-label={`Highlight color ${swatch.name}`}
                  onClick={() => setSelectedColor(swatch.main)}
                  sx={{
                    ...buttonReset,
                    width: t.swatches.size,
                    height: t.swatches.size,
                    borderRadius: t.swatches.radius,
                    background: swatch.main,
                    color: swatch.check,
                    boxShadow: active
                      ? `${t.swatches.activeRingInner}, 0 0 0 ${t.swatches.activeRingWidth} ${swatch.main}`
                      : t.swatches.restRing,
                  }}
                >
                  {active && <SwatchCheckIcon size={t.swatches.checkSize} />}
                </Box>
              );
            })}
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: t.swatches.hintFontSize,
              color: t.swatches.hintColor,
              lineHeight: t.swatches.hintLineHeight,
            }}
          >
            Pick a color, then click clauses in the document to flag them. Click
            a flagged clause again to clear it.
          </Typography>
        </Box>

        {/* Flag count */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: t.flagCard.gap,
            padding: t.flagCard.padding,
            background: t.flagCard.background,
            border: t.flagCard.border,
            borderRadius: t.flagCard.radius,
          }}
        >
          <Box
            sx={{
              width: t.flagCard.iconBoxSize,
              height: t.flagCard.iconBoxSize,
              borderRadius: t.flagCard.iconBoxRadius,
              background: t.flagCard.iconBoxBg,
              color: t.flagCard.iconColor,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HighlighterIcon size={t.flagCard.iconSize} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontSize: t.flagCard.countFontSize,
                fontWeight: t.flagCard.countFontWeight,
                color: t.flagCard.countColor,
                lineHeight: t.lineHeight,
              }}
            >
              {totalHighlights}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: t.flagCard.labelFontSize,
                color: t.flagCard.labelColor,
                lineHeight: t.lineHeight,
              }}
            >
              {flagLabel}
            </Typography>
          </Box>
          {hasFlags && (
            <Box
              component="button"
              type="button"
              onClick={clearAllHighlights}
              sx={{
                ...buttonReset,
                marginLeft: "auto",
                flexShrink: 0,
                height: t.flagCard.clearHeight,
                px: t.flagCard.clearPaddingX,
                borderRadius: t.flagCard.clearRadius,
                border: `${t.flagCard.clearBorderWidth} solid ${c.border}`,
                background: c.white,
                color: c.meta,
                fontSize: t.flagCard.clearFontSize,
                fontWeight: t.flagCard.clearFontWeight,
                "&:hover": {
                  color: c.danger,
                  borderColor: c.dangerBorderStrong,
                },
              }}
            >
              Clear all
            </Box>
          )}
        </Box>

        {/* Common reasons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: t.chips.blockGap,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: t.rail.labelFontSize,
              fontWeight: t.rail.labelFontWeight,
              color: t.rail.labelColor,
              lineHeight: t.lineHeight,
            }}
          >
            Common reasons
          </Typography>
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: t.chips.rowGap }}
          >
            {COMMON_REASONS.map((txt) => {
              const active = reason === txt;
              return (
                <Box
                  key={txt}
                  component="button"
                  type="button"
                  onClick={() => setReason(txt)}
                  sx={{
                    ...buttonReset,
                    height: t.chips.height,
                    px: t.chips.paddingX,
                    borderRadius: t.chips.radius,
                    border: `${t.chips.borderWidth} solid ${
                      active ? c.danger : c.border
                    }`,
                    background: active ? c.dangerTint : c.white,
                    color: active ? c.danger : c.meta,
                    fontSize: t.chips.fontSize,
                    fontWeight: t.chips.fontWeight,
                    whiteSpace: "nowrap",
                  }}
                >
                  {txt}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Reason */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: t.reason.blockGap,
          }}
        >
          <Box
            component="label"
            htmlFor="declineReason"
            sx={{
              fontSize: t.rail.labelFontSize,
              fontWeight: t.rail.labelFontWeight,
              color: t.rail.labelColor,
              lineHeight: t.lineHeight,
            }}
          >
            Reason{" "}
            <Box component="span" sx={{ color: c.accent }}>
              *
            </Box>
          </Box>
          <Box
            component="textarea"
            id="declineReason"
            name="declineReason"
            rows={t.reason.rows}
            maxLength={t.reason.maxLength}
            placeholder="Explain what needs to change. Flagged clauses are attached automatically."
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setReason(e.target.value)
            }
            sx={{
              padding: t.reason.padding,
              border: `${t.reason.borderWidth} solid ${c.border}`,
              borderRadius: t.reason.radius,
              background: c.white,
              fontFamily: "inherit",
              fontSize: t.reason.fontSize,
              lineHeight: t.lineHeight,
              color: t.reason.color,
              resize: "vertical",
              boxSizing: "border-box",
              "&::placeholder": { color: c.placeholder },
              "&:focus": { borderColor: c.accent, outline: "none" },
            }}
          />
          <Typography
            component="span"
            sx={{
              alignSelf: "flex-end",
              fontSize: t.reason.counterFontSize,
              fontWeight: t.reason.counterFontWeight,
              color: t.reason.counterColor,
              lineHeight: t.lineHeight,
            }}
          >
            {reason.length} / {t.reason.maxLength}
          </Typography>
        </Box>

        {/* Consequence callout */}
        <Box
          sx={{
            display: "flex",
            gap: t.warning.gap,
            padding: t.warning.padding,
            background: t.warning.background,
            border: t.warning.border,
            borderRadius: t.warning.radius,
          }}
        >
          <Box
            sx={{
              color: c.danger,
              flexShrink: 0,
              marginTop: t.warning.iconMarginTop,
            }}
          >
            <AlertCircleIcon size={t.warning.iconSize} />
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: t.warning.fontSize,
              lineHeight: t.warning.lineHeight,
              color: c.dangerText,
            }}
          >
            Declining ends this request for all signers.{" "}
            {senderName || "The sender"} will need to send a new one.
          </Typography>
        </Box>
      </Box>

      {/* Footer actions */}
      <Box
        sx={{
          flexShrink: 0,
          padding: t.footer.padding,
          borderTop: t.footer.borderTop,
          display: "flex",
          gap: t.footer.gap,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => history.goBack()}
          sx={{
            ...buttonReset,
            flex: "none",
            height: t.footer.height,
            px: t.footer.cancelPaddingX,
            borderRadius: t.footer.radius,
            border: `${t.footer.cancelBorderWidth} solid ${c.border}`,
            background: c.white,
            color: c.body,
            fontSize: t.footer.fontSize,
            fontWeight: t.footer.fontWeight,
            "&:hover": { background: c.hover },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleDecline}
          disabled={!canDecline}
          sx={{
            ...buttonReset,
            flex: 1,
            height: t.footer.height,
            borderRadius: t.footer.radius,
            background: canDecline ? c.danger : c.disabledFill,
            color: c.white,
            fontSize: t.footer.fontSize,
            fontWeight: t.footer.fontWeight,
            cursor: canDecline ? "pointer" : "not-allowed",
          }}
        >
          {declineLoading ? "Declining..." : "Decline request"}
        </Box>
      </Box>
    </Box>
  );

  if (IS_EMBED && embedDeclined) {
    return (
      <EmbedDone
        title="You declined this document."
        message="You can close this window."
      />
    );
  }

  if (pageLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: c.canvas,
        }}
      >
        <CircularProgress aria-label="Loading" />
        <Typography sx={{ ml: 2, color: c.meta }}>Loading...</Typography>
      </Box>
    );
  }

  if (!requestToken || pdfFiles.length === 0) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: c.canvas,
        }}
      >
        <Typography
          sx={{
            fontSize: t.header.titleFontSize,
            fontWeight: t.header.titleFontWeight,
            color: c.ink,
          }}
        >
          No documents found
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => history.goBack()}
          sx={{
            ...buttonReset,
            height: t.footer.height,
            px: t.footer.cancelPaddingX,
            borderRadius: t.footer.radius,
            border: `${t.footer.cancelBorderWidth} solid ${c.border}`,
            background: c.white,
            color: c.body,
            fontSize: t.footer.fontSize,
            fontWeight: t.footer.fontWeight,
            "&:hover": { background: c.hover },
          }}
        >
          Go back
        </Box>
      </Box>
    );
  }

  const curIdx = selectedFile
    ? pdfFiles.findIndex((f) => getFileId(f) === getFileId(selectedFile))
    : -1;
  const curName = selectedFile
    ? selectedFile.unsignedPdf ||
      selectedFile.unsignedDoc ||
      `Document ${curIdx + 1}`
    : "";
  const curFlagged = selectedFile
    ? (highlightsByFile.get(getFileId(selectedFile)) || []).length > 0
    : false;
  const hasPrev = curIdx > 0;
  const hasNext = curIdx >= 0 && curIdx < pdfFiles.length - 1;

  const navButtonSx = (enabled: boolean) => ({
    ...buttonReset,
    width: t.canvas.navButtonSize,
    height: t.canvas.navButtonSize,
    borderRadius: t.canvas.navButtonRadius,
    border: t.canvas.navButtonBorder,
    background: c.white,
    color: enabled ? c.body : c.disabledInk,
    cursor: enabled ? "pointer" : "default",
    flexShrink: 0,
    "&:hover": { background: enabled ? c.hover : c.white },
  });

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: c.canvas,
        overflow: "hidden",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {/* Loading Overlay */}
      <CustomLoader isLoading={declineLoading} />

      {/* Header */}
      <Box
        component="header"
        sx={{
          height: t.header.height,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: t.header.gap,
          px: t.header.paddingX,
          borderBottom: t.header.borderBottom,
          background: t.header.background,
          boxSizing: "border-box",
          zIndex: 20,
        }}
      >
        <Box
          component="button"
          type="button"
          title="Back"
          onClick={() => history.goBack()}
          sx={{
            ...buttonReset,
            width: t.header.backSize,
            height: t.header.backSize,
            borderRadius: t.header.backRadius,
            color: t.header.backColor,
            flexShrink: 0,
            "&:hover": { background: t.header.backHoverBg },
          }}
        >
          <ArrowLeftIcon size={t.header.backIconSize} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: t.header.titleGap,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: t.header.titleFontSize,
              fontWeight: t.header.titleFontWeight,
              color: t.header.titleColor,
              lineHeight: t.lineHeight,
            }}
          >
            Decline request
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: t.header.subtitleFontSize,
              color: t.header.subtitleColor,
              lineHeight: t.lineHeight,
            }}
          >
            Highlight the clauses you're not comfortable with, then tell{" "}
            {senderName || "the sender"} why.
          </Typography>
        </Box>
        {isMobile && (
          <Box
            component="button"
            type="button"
            title="Decline options"
            onClick={() => setIsDocumentsDrawerOpen(true)}
            sx={{
              ...buttonReset,
              width: t.header.backSize,
              height: t.header.backSize,
              borderRadius: t.header.backRadius,
              color: t.header.backColor,
              flexShrink: 0,
              "&:hover": { background: t.header.backHoverBg },
            }}
          >
            <FileIcon size={t.header.backIconSize} />
          </Box>
        )}
      </Box>

      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flex: 1, display: "flex", minHeight: 0, outline: "none" }}
      >
        {/* Document canvas */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: t.canvas.background,
            pt: t.canvas.paddingTop,
            px: t.canvas.paddingX,
            pb: t.canvas.paddingBottom,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: t.canvas.contentMaxWidth,
              mx: "auto",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Document switcher */}
            {selectedFile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: t.canvas.toolbarGap,
                  mb: t.canvas.toolbarMarginBottom,
                  flexShrink: 0,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  title="Previous document"
                  onClick={() => hasPrev && handleFileSelect(pdfFiles[curIdx - 1])}
                  sx={navButtonSx(hasPrev)}
                >
                  <ChevronLeftIcon size={t.canvas.navIconSize} />
                </Box>

                <Box
                  component="button"
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                    setDocMenuAnchor(e.currentTarget)
                  }
                  sx={{
                    ...buttonReset,
                    height: t.canvas.docButtonHeight,
                    maxWidth: t.canvas.docButtonMaxWidth,
                    minWidth: 0,
                    px: t.canvas.docButtonPaddingX,
                    borderRadius: t.canvas.docButtonRadius,
                    border: `${t.canvas.docButtonBorderWidth} solid ${c.border}`,
                    background: c.white,
                    color: c.ink,
                    fontSize: t.canvas.docButtonFontSize,
                    fontWeight: t.canvas.docButtonFontWeight,
                    gap: t.canvas.docButtonGap,
                    "&:hover": { borderColor: c.borderStrong },
                  }}
                >
                  <Box sx={{ color: c.accent, flexShrink: 0, display: "flex" }}>
                    <FileIcon size={t.canvas.docIconSize} />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {curName}
                  </Box>
                  {curFlagged && (
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        width: t.canvas.docFlagDotSize,
                        height: t.canvas.docFlagDotSize,
                        borderRadius: "50%",
                        background: c.accent,
                      }}
                    />
                  )}
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontSize: t.canvas.docPosFontSize,
                      fontWeight: t.canvas.docPosFontWeight,
                      color: c.muted,
                    }}
                  >
                    {curIdx + 1} of {pdfFiles.length}
                  </Box>
                  <Box sx={{ color: c.muted, flexShrink: 0, display: "flex" }}>
                    <CaretDownIcon size={t.canvas.caretSize} />
                  </Box>
                </Box>

                <Box
                  component="button"
                  type="button"
                  title="Next document"
                  onClick={() => hasNext && handleFileSelect(pdfFiles[curIdx + 1])}
                  sx={navButtonSx(hasNext)}
                >
                  <ChevronRightIcon size={t.canvas.navIconSize} />
                </Box>

                <Typography
                  component="span"
                  sx={{
                    ml: "auto",
                    flexShrink: 0,
                    fontSize: t.canvas.hintFontSize,
                    color: t.canvas.hintColor,
                    lineHeight: t.lineHeight,
                  }}
                >
                  click a clause to flag it
                </Typography>

                <Menu
                  anchorEl={docMenuAnchor}
                  open={Boolean(docMenuAnchor)}
                  onClose={() => setDocMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      mt: "6px",
                      width: t.docMenu.width,
                      maxWidth: t.docMenu.width,
                      maxHeight: t.docMenu.maxHeight,
                      border: t.docMenu.border,
                      borderRadius: t.docMenu.radius,
                      boxShadow: t.docMenu.shadow,
                      padding: t.docMenu.padding,
                    },
                  }}
                  MenuListProps={{ sx: { p: 0 } }}
                >
                  {pdfFiles.map((file, i) => {
                    const name =
                      file.unsignedPdf || file.unsignedDoc || `Document ${i + 1}`;
                    const flags = (highlightsByFile.get(getFileId(file)) || [])
                      .length;
                    return (
                      <MenuItem
                        key={getFileId(file) || i}
                        selected={i === curIdx}
                        onClick={() => {
                          handleFileSelect(file);
                          setDocMenuAnchor(null);
                        }}
                        sx={{
                          padding: t.docMenu.itemPadding,
                          borderRadius: t.docMenu.itemRadius,
                          gap: t.docMenu.itemGap,
                          minHeight: "auto",
                          lineHeight: t.docMenu.itemLineHeight,
                          "&.Mui-selected, &.Mui-selected:hover": {
                            background: c.dangerTint,
                          },
                          "&:hover": { background: c.railHover },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: t.docMenu.numWidth,
                            flexShrink: 0,
                            fontSize: t.docMenu.numFontSize,
                            fontWeight: t.docMenu.numFontWeight,
                            color: c.muted,
                          }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: t.docMenu.nameFontSize,
                            fontWeight: t.docMenu.nameFontWeight,
                            color: c.ink,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </Box>
                        {flags > 0 && (
                          <Box
                            component="span"
                            sx={{
                              flexShrink: 0,
                              fontSize: t.docMenu.badgeFontSize,
                              fontWeight: t.docMenu.badgeFontWeight,
                              color: c.danger,
                              background: c.dangerTint,
                              border: `1px solid ${c.dangerBorder}`,
                              borderRadius: t.docMenu.badgeRadius,
                              padding: t.docMenu.badgePadding,
                            }}
                          >
                            {flags} flagged
                          </Box>
                        )}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Box>
            )}

            {/* Document sheet — the PDF renders into it */}
            <Box
              ref={pdfContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                background: c.white,
                border: t.canvas.sheetBorder,
                borderRadius: t.canvas.sheetRadius,
                boxShadow: t.canvas.sheetShadow,
                position: "relative",
                // The in-progress selection colour is owned by PDFHighlighter,
                // which shares one alpha with the committed highlight. Setting
                // it a second time here only let the two drift apart.
                "& div": {
                  "&::-webkit-scrollbar": { display: "auto" },
                  "& > .pdfViewer": {
                    paddingBottom: "80px !important",
                  },
                },
              }}
            >
              {loading && !pdfUrl ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress aria-label="Loading" />
                  <Typography sx={{ mt: 2, color: c.meta }}>
                    Loading PDF...
                  </Typography>
                </Box>
              ) : pdfUrl ? (
                <PDFEditor
                  key={currentFileKey}
                  pdfUrl={pdfUrl}
                  initialHighlights={frozenInitialHighlights}
                  currentColor={selectedColor}
                  onHighlightsChange={updateCurrentFileHighlights}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: c.muted }}>
                    Select a document to view
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Desktop: always-visible control rail */}
        {!isMobile && renderRail()}
      </Box>

      {/* Mobile: swipeable rail */}
      {isMobile && (
        <SwipeableDrawer
          anchor="right"
          open={isDocumentsDrawerOpen}
          onClose={() => setIsDocumentsDrawerOpen(false)}
          onOpen={() => setIsDocumentsDrawerOpen(true)}
          PaperProps={{
            sx: {
              width: "85%",
              maxWidth: t.rail.width,
            },
          }}
        >
          {renderRail()}
        </SwipeableDrawer>
      )}
    </Box>
  );
};

export default DeclineRequestPage;
